import * as SliderPrimitive from "@radix-ui/react-slider";
import {
  animate,
  useReducedMotion,
  type AnimationPlaybackControls,
} from "motion/react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { hexToRgba } from "../../utils/colorUtils";
import GiftCardGIFTRRedeemInput from "./GiftCardGIFTRRedeemInput";

export interface GiftCardRedeemAmountSliderProps {
  value: number;
  max: number;
  min?: number;
  step?: number;
  onChange: (value: number) => void;
  accentColor: string;
  label?: string;
  useFullLabel?: string;
  disabled?: boolean;
  /**
   * Holds the shimmer in the track's place until the balance lands. The header stays put so nothing
   * shifts, and the intro count-up is armed off this flag so it plays against the real balance
   * rather than the zero we render while waiting.
   */
  loading?: boolean;
  loadingLabel?: string;
  /**
   * Whether this balance is worth counting up to. Switching cards remounts the slider, so the
   * caller — which outlives the mount — decides when a balance is new to the shopper.
   */
  playIntro?: boolean;
  /**
   * Hands the caret to the amount field while it is set. The caller owns switching it back off —
   * `GiftProvider` clears it at the top of every call that could raise it again.
   */
  focusInput?: boolean;
  /**
   * Drops the track when there is no balance to divide, rather than showing a disabled 0/0 one.
   * The header row stays either way, so the label keeps its place. Off by default: a caller that
   * has a lookup behind the balance wants the empty track holding the slider's place, and only one
   * that knows the balance is settled can drop it.
   */
  hideOnEmptyBalance?: boolean;
  /**
   * True inside the merchant dashboard's customise-UI preview. Autofocusing the amount field there
   * steals focus (and scrolls) in a page the shopper never sees, so `focusInput` is ignored while
   * this is set.
   */
  dashboardOnly?: boolean;
}

const clamp = (next: number, min: number, max: number) =>
  Math.min(max, Math.max(min, next));
const INTRO_DURATION = 0.8;
const INTRO_EASE = [0.16, 1, 0.3, 1] as const;
// Use Full is a jump the shopper did not drag, so the bar and the number travel there instead of
// snapping — short enough that it never sits between the tap and the apply.
const FILL_DURATION = 0.4;
const THUMB_FADE_LOW_FILL_RATIO = 0.15;
const THUMB_FADE_HIGH_FILL_RATIO = 0.8;

const GiftCardRedeemAmountSlider = ({
  value,
  max,
  min = 0,
  step = 1,
  onChange,
  accentColor,
  label = "Redeem Amount",
  useFullLabel = "Use Full",
  disabled = false,
  loading = false,
  loadingLabel,
  playIntro = true,
  focusInput = false,
  hideOnEmptyBalance = false,
  dashboardOnly = false,
}: GiftCardRedeemAmountSliderProps) => {
  const reduceMotion = useReducedMotion();
  const amountInputRef = useRef<HTMLInputElement>(null);

  // The fill drives the real value frame by frame, so the bar, the number and whatever the host
  // does with the amount never disagree — and any touch of the control cuts it short.
  const fillControls = useRef<AnimationPlaybackControls | null>(null);
  const stopFill = () => {
    fillControls.current?.stop();
    fillControls.current = null;
  };

  useEffect(() => stopFill, []);

  const handleTypedAmountChange = (raw: string) => {
    stopFill();
    const digits = raw.replace(/\D/g, "");
    onChange(digits === "" ? min : clamp(Number(digits), min, max));
  };

  const isRangeEmpty = !Number.isFinite(max) || max <= min;
  const isDisabled = disabled || isRangeEmpty;
  const sliderMax = isRangeEmpty ? min + 1 : max;
  const sliderValue = clamp(value, min, sliderMax);
  const displayMax = Number.isFinite(max) ? max : min;

  const introTarget = useRef({ amount: sliderValue, max: displayMax });
  const introControls = useRef<AnimationPlaybackControls | null>(null);
  const introArmed = useRef(false);
  const [introProgress, setIntroProgress] = useState<number | null>(null);
  const isIntroPlaying = introProgress !== null;
  const [isHandleActive, setIsHandleActive] = useState(false);
  // Arms once, on the first render that has a balance to count up to — mount when there is no
  // lookup, otherwise the render where `loading` drops. Layout effect so the first painted frame is
  // already the start of the count-up and never the final number.
  useLayoutEffect(() => {
    if (loading || !playIntro || introArmed.current) return;

    // Nothing to count up to yet — stay unarmed so the balance that lands later still gets its run.
    const target = { amount: sliderValue, max: displayMax };
    if (target.amount <= min) return;

    introArmed.current = true;
    introTarget.current = target;
    if (reduceMotion) return;

    setIntroProgress(0);
    introControls.current = animate(0, 1, {
      duration: INTRO_DURATION,
      ease: INTRO_EASE,
      onUpdate: (latest) => setIntroProgress(latest),
      onComplete: () => setIntroProgress(null),
    });

    return () => {
      introControls.current?.stop();
      introControls.current = null;
      // Whatever re-ran this effect — StrictMode's remount, `loading` flipping back — has to be
      // able to arm again. Left armed, `introProgress` strands at 0 and the amount reads 0.
      introArmed.current = false;
      setIntroProgress(null);
    };
  }, [loading]);

  useEffect(() => {
    if (!isIntroPlaying) return;
    if (
      sliderValue !== introTarget.current.amount ||
      displayMax !== introTarget.current.max
    ) {
      introControls.current?.stop();
      setIntroProgress(null);
    }
  }, [isIntroPlaying, sliderValue, displayMax]);

  useEffect(() => {
    if (!focusInput || isDisabled || loading || dashboardOnly) return;
    amountInputRef.current?.focus();
  }, [focusInput, isDisabled, isIntroPlaying, loading, dashboardOnly]);

  const handleUseFull = () => {
    stopFill();
    if (reduceMotion || sliderValue >= displayMax) {
      onChange(max);
      return;
    }

    fillControls.current = animate(sliderValue, max, {
      duration: FILL_DURATION,
      ease: INTRO_EASE,
      onUpdate: (latest) => onChange(Math.round(latest)),
      onComplete: () => onChange(max),
    });
  };

  const countUpTo = (target: number) =>
    Math.round(min + (target - min) * (introProgress ?? 1));
  const displayAmount = isIntroPlaying
    ? countUpTo(introTarget.current.amount)
    : sliderValue;
  const fillRatio =
    sliderMax > min ? (displayAmount - min) / (sliderMax - min) : 0;
  const isThumbFaded =
    fillRatio < THUMB_FADE_LOW_FILL_RATIO ||
    fillRatio > THUMB_FADE_HIGH_FILL_RATIO;

  const focusAmountInput = () => {
    if (isDisabled || loading || dashboardOnly || isIntroPlaying) return;
    amountInputRef.current?.focus();
  };

  // Radix captures the pointer for the drag, so losing that capture is the release — whether the
  // shopper lifted the pointer or the browser cancelled the touch.
  const handleSliderPointerDown = () => {
    if (isDisabled) return;
    stopFill();
    focusAmountInput();
    setIsHandleActive(true);
  };

  return (
    <div
      className={`ui-flex ui-w-full ui-flex-col ui-gap-1${isIntroPlaying ? " ui-pointer-events-none" : ""}`}
    >
      {!(hideOnEmptyBalance && isRangeEmpty && !loading) && (
        <div className="ui-flex ui-w-full ui-items-center ui-justify-between ui-px-1">
          <p className="ui-text-[12px] ui-font-medium ui-leading-[18px] ui-tracking-[-0.12px] ui-text-[#71717b]">
            {label}
          </p>
          <button
            type="button"
            disabled={loading || isDisabled || value === displayMax}
            onClick={handleUseFull}
            className="ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px] disabled:ui-opacity-60"
            style={{ color: accentColor }}
          >
            {useFullLabel}
          </button>
        </div>
      )}

      {loading ? (
        <GiftCardGIFTRRedeemInput
          accentColor={accentColor}
          label={loadingLabel}
        />
      ) : // A lookup in flight is not an empty balance — `loading` still owes a number, so it keeps
      // the shimmer above rather than dropping the track mid-request. Only a settled zero drops it.
      hideOnEmptyBalance && isRangeEmpty ? null : (
        <SliderPrimitive.Root
          value={[displayAmount]}
          min={min}
          max={sliderMax}
          step={step}
          disabled={isDisabled}
          onPointerDown={handleSliderPointerDown}
          onLostPointerCapture={() => setIsHandleActive(false)}
          onValueChange={(next) => {
            onChange(next[0] ?? value);
            focusAmountInput();
          }}
          className={`ui-relative ui-flex ui-h-[48px] ui-w-full ui-touch-none ui-select-none ui-items-center ui-overflow-hidden ui-rounded-[10px] ui-bg-[#f4f4f5]${isDisabled ? " ui-cursor-not-allowed" : isHandleActive ? " ui-cursor-grabbing" : " ui-cursor-grab"}`}
        >
          <SliderPrimitive.Track className="ui-relative ui-h-full ui-w-full ui-grow">
            <SliderPrimitive.Range
              className="ui-absolute ui-inset-y-0 ui-left-0 ui-h-full ui-rounded-r-[8px]"
              style={{ backgroundColor: hexToRgba(accentColor, 0.12) }}
            />
          </SliderPrimitive.Track>
          <SliderPrimitive.Thumb
            className={`ui-relative ui-block ui-h-[16px] ui-w-[4px] ui-shrink-0 ui-origin-center ui-rounded-full ui-transition-[transform,opacity] ui-duration-200 ui-ease-out${isHandleActive ? " motion-safe:ui-scale-125" : ""}${isThumbFaded ? " ui-pointer-events-none ui-opacity-0" : " ui-opacity-100"}`}
            style={{
              backgroundColor: accentColor,
              right: displayAmount > min ? 6 : 0,
            }}
          />

          <div className="ui-pointer-events-none ui-absolute ui-inset-0 ui-flex ui-items-center ui-justify-between ui-px-[14px]">
            <input
              ref={amountInputRef}
              type="text"
              inputMode="numeric"
              disabled={isDisabled}
              value={displayAmount}
              onChange={(e) => handleTypedAmountChange(e.target.value)}
              onPointerDown={(e) => e.stopPropagation()}
              className="ui-pointer-events-auto ui-cursor-text ui-border-0 ui-bg-transparent ui-p-0 ui-text-[16px] ui-font-medium ui-leading-[24px] ui-tracking-[-0.16px] ui-outline-none"
              style={{
                color: accentColor,
                width: `${String(displayAmount).length + 1}ch`,
              }}
            />
            <p className="ui-text-text-md-regular ui-text-zinc-800 ui-opacity-60">
              /{displayMax}
            </p>
          </div>
        </SliderPrimitive.Root>
      )}
    </div>
  );
};

export default GiftCardRedeemAmountSlider;
